import { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { TApi, TRoutes, TSupporterRoles } from '../../shared/const'
import http from '../../shared/http'
import i18nInstance from '../../shared/i18n'
import { emptyJuniorAlumni, juniorAlumniErrors, juniorAlumniPayload } from '../../shared/juniorAlumni'

import { fullLogIn } from '../../shared/helpers'

import Btn from '../../UI/Btn'
import GdprConsentNote from '../../UI/GdprConsentNote'
import JuniorAlumniField from '../../UI/JuniorAlumniField'
import { PlainInput } from '../../UI/PlainField'
import StatesField from '../../UI/StatesField'
import Loading from '../../UI/Loading'

import styles from './style.module.scss'

/**
 * Abschluss einer Einladung: Passwort setzen und Rollen bestaetigen.
 *
 * Oeffentliche Seite - die Person hat hier noch keinen Login. Der Token aus dem
 * Einladungslink wird zuerst geprueft, damit ein abgelaufener Link nicht erst
 * nach dem Ausfuellen des Formulars auffaellt.
 */
export default function SupporterActivation() {
	const { t } = useTranslation()
	const { token } = useParams()
	const history = useHistory()

	const [invitation, setInvitation] = useState(null)
	const [invalid, setInvalid] = useState(false)
	const [isSaving, setSaving] = useState(false)

	const [values, setValues] = useState({
		firstName: '',
		lastName: '',
		company: '',
		position: '',
		password: '',
		repeat: ''
	})
	const [states, setStates] = useState([])
	const [roles, setRoles] = useState([TSupporterRoles.EXPERT])
	// Dieselbe Frage wie Abschnitt 4 der Registrierung (Anforderung #181).
	const [alumni, setAlumni] = useState(emptyJuniorAlumni)
	const [gdpr, setGdpr] = useState(false)
	const [errors, setErrors] = useState({})

	// Die App stellt die Sprache im Navbar auf Deutsch. Diese Seite laeuft
	// ausserhalb davon (oeffentliche Route ohne Layout), deshalb muss es hier
	// selbst passieren - sonst greift die Browsererkennung und die Seite
	// erscheint auf Englisch.
	//
	// Bewusst ueber die konfigurierte Instanz statt ueber useTranslation():
	// ohne initialisiertes i18next liefert der Hook nur einen Platzhalter ohne
	// changeLanguage.
	useEffect(() => {
		if (i18nInstance.language !== 'ger') i18nInstance.changeLanguage('ger')
	}, [])

	useEffect(() => {
		// Die Seite erklärt selbst, was mit dem Link ist — ohne disableToast
		// stünde dieselbe Meldung zusätzlich als Hinweis oben.
		http.get(TApi.PUBLIC_SUPPORTER_INVITATION(token), { disableToast: true })
			.then(({ data }) => {
				setInvitation(data)
				setValues(current => ({
					...current,
					firstName: data.firstName || '',
					lastName: data.lastName || '',
					company: data.company || '',
					position: data.position || ''
				}))
				if (data.roles?.length) setRoles(data.roles)
			})
			// 410: Der Link war gültig, die 60 Tage sind aber vorbei. Das ist
			// etwas anderes als ein falscher oder schon benutzter Link — die
			// Person kann sich eine neue Einladung schicken lassen.
			.catch(error => setInvalid(error?.response?.status === 410 ? 'expired' : 'invalid'))
	}, [token])

	if (invalid) {
		return (
			<div className={styles.root}>
				<div className={styles.card}>
					<h1 className={styles.title}>{t('activateProfile')}</h1>
					<p className={styles.error}>
						{invalid === 'expired'
							? t('supporterInvitationExpired')
							: t('This invitation link is invalid or has already been used')}
					</p>
					<Btn onClick={() => history.push(TRoutes.SIGN_IN)}>{t('Login')}</Btn>
				</div>
			</div>
		)
	}

	if (!invitation) return <Loading />

	const setValue = (key, value) => setValues(current => ({ ...current, [key]: value }))

	const toggleRole = role =>
		setRoles(current =>
			current.includes(role) ? current.filter(item => item !== role) : [...current, role]
		)

	const submit = event => {
		event.preventDefault()

		const next = {}
		if (values.firstName.trim().length < 2) next.firstName = t('Required field')
		if (values.lastName.trim().length < 2) next.lastName = t('Required field')
		if (values.password.length < 8) next.password = t('Password must be at least 8 characters')
		if (values.password !== values.repeat) next.repeat = t('Passwords do not match')
		if (!roles.length) next.roles = t('Required field')
		if (!gdpr) next.gdpr = t('Required field')
		const alumniErrors = juniorAlumniErrors(alumni)
		if (Object.keys(alumniErrors).length) next.alumni = alumniErrors

		setErrors(next)
		if (Object.keys(next).length) return

		setSaving(true)
		http.post(TApi.PUBLIC_SUPPORTER_ACTIVATE, {
			token,
			firstName: values.firstName,
			lastName: values.lastName,
			company: values.company || null,
			position: values.position || null,
			password: values.password,
			roles,
			targetStates: states,
			...juniorAlumniPayload(alumni),
			gdprConsent: true
		})
			.then(async ({ data }) => {
				// Der Server liefert direkt ein Token - die Person ist nach der
				// Aktivierung sofort angemeldet und muss sich nicht neu einloggen.
				// fullLogIn setzt Header, Redux-State und localStorage einheitlich;
				// ein eigenes localStorage.setItem wuerde am Schluessel 'auth'
				// vorbeischreiben und die App weiterhin ausgeloggt lassen.
				http.defaults.headers['Authorization'] = `Bearer ${data.token}`
				const { data: profile } = await http.get(TApi.COMMON_ME)

				fullLogIn(data.token, profile)
				window.location.href = TRoutes.SUPPORTER_PROFILE
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	return (
		<div className={styles.root}>
			<form className={styles.card} onSubmit={submit}>
				<h1 className={styles.title}>{t('activateProfile')}</h1>
				<p className={styles.subtitle}>{invitation.email}</p>

				<div className={styles.grid}>
					<PlainInput
						label={t('firstName')}
						value={values.firstName}
						error={errors.firstName}
						onChange={e => setValue('firstName', e.target.value)}
					/>
					<PlainInput
						label={t('lastName')}
						value={values.lastName}
						error={errors.lastName}
						onChange={e => setValue('lastName', e.target.value)}
					/>
					<PlainInput
						label={t('company')}
						value={values.company}
						onChange={e => setValue('company', e.target.value)}
					/>
					<PlainInput
						label={t('position')}
						value={values.position}
						onChange={e => setValue('position', e.target.value)}
					/>
					<PlainInput
						label={t('password')}
						type="password"
						value={values.password}
						error={errors.password}
						onChange={e => setValue('password', e.target.value)}
					/>
					<PlainInput
						label={t('repeatPassword')}
						type="password"
						value={values.repeat}
						error={errors.repeat}
						onChange={e => setValue('repeat', e.target.value)}
					/>
				</div>

				<StatesField value={states} onChange={setStates} />

				<span className={styles.label}>{t('possibleRoles')}</span>
				<div className={styles.roles}>
					{Object.values(TSupporterRoles).map(role => (
						<button
							key={role}
							type="button"
							className={clsx(styles.role, roles.includes(role) && styles.roleOn)}
							onClick={() => toggleRole(role)}
						>
							{t(`supporterRole${role}`)}
						</button>
					))}
				</div>
				{errors.roles && <span className={styles.error}>{errors.roles}</span>}

				<h2 className={styles.sectionTitle}>{t('juniorAlumniTitle')}</h2>
				<JuniorAlumniField
					value={alumni}
					errors={errors.alumni}
					onChange={changes => setAlumni(current => ({ ...current, ...changes }))}
				/>

				<label className={styles.checkboxRow}>
					<input type="checkbox" checked={gdpr} onChange={e => setGdpr(e.target.checked)} />
					{t('gdprConsent')}
				</label>
				<GdprConsentNote className={styles.consentNote} />
				{errors.gdpr && <span className={styles.error}>{errors.gdpr}</span>}

				<Btn type="submit" disabled={isSaving} className={styles.submit}>
					{t('activateProfile')}
				</Btn>
			</form>
		</div>
	)
}
